import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import AssessmentSteps from '@/components/assessment/AssessmentSteps';
import RoleDetailsStep from '@/components/assessment/RoleDetailsStep';
import SkillsSelectionStep from '@/components/assessment/SkillsSelectionStep';
import QuestionsStep from '@/components/assessment/QuestionsStep';
import CompletionStep from '@/components/assessment/CompletionStep';
import AssessmentTypeStep, { AssessmentType } from '@/components/assessment/AssessmentTypeStep';
import CustomAssessmentConfig, { CustomAssessmentConfig as CustomConfig } from '@/components/assessment/CustomAssessmentConfig';
import AIAssessmentSettingsStep, { InterviewerSettings } from '@/components/assessment/AIAssessmentSettingsStep';
import { getUserCredits, useCreditsForAssessment } from '@/services/creditService';
import { getUserSubscription, isBasicPlan, Subscription } from '@/services/subscriptionService';
import { AnimatePresence, motion } from 'framer-motion';
import AIProcessingAnimation from '@/components/assessment/AIProcessingAnimation';
import { encryptCandidateDetails } from '@/utils/encryption';

export type AssessmentData = {
  role: string;
  level: 'entry' | 'mid' | 'senior';
  selectedSkills: string[];
  customQuestions: string[];
  source?: 'RoundOne' | 'ATS';
  atsJobId?: string;
  assessmentType?: AssessmentType;
  language?: string;
  accent?: string;
  voiceId?: string;
  skillQuestions?: Record<string, string[]>;
};

const CreateAssessment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  
  // Get job data if passed from ATS Jobs page
  const locationState = location.state as {
    jobTitle?: string;
    source?: 'RoundOne' | 'ATS';
    atsJobId?: string;
  } | undefined;
  
  const [assessmentData, setAssessmentData] = useState<AssessmentData>({
    role: locationState?.jobTitle || '',
    level: 'mid',
    selectedSkills: [],
    customQuestions: [],
    source: locationState?.source || 'RoundOne',
    atsJobId: locationState?.atsJobId,
    assessmentType: undefined,
    language: 'en',
    accent: 'us',
    voiceId: undefined,
    skillQuestions: {}
  });
  
  // Add interviewer settings state for AI assisted flow
  const [interviewerSettings, setInterviewerSettings] = useState<InterviewerSettings>({
    language: 'English',
    accent: 'American',
    voiceId: 'g6xIsTj2HwM6VR4iXFCw'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userCredits, setUserCredits] = useState<number | null>(null);
  const [userSubscription, setUserSubscription] = useState<Subscription | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);
  const [showAIProcessing, setShowAIProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState({ title: '', description: '' });
  const [createdAssessmentId, setCreatedAssessmentId] = useState<string>();

  useEffect(() => {
    if (user) {
      fetchUserCredits();
      fetchUserSubscription();
    }
  }, [user]);

  const fetchUserCredits = async () => {
    if (!user) return;
    
    setIsLoadingCredits(true);
    try {
      const credits = await getUserCredits(user.id);
      setUserCredits(credits?.balance || 0);
    } catch (error) {
      console.error('Error fetching user credits:', error);
    } finally {
      setIsLoadingCredits(false);
    }
  };

  const fetchUserSubscription = async () => {
    if (!user) return;
    
    try {
      const subscription = await getUserSubscription(user.id);
      setUserSubscription(subscription);
    } catch (error) {
      console.error('Error fetching user subscription:', error);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/assessments');
    }
  };

  const handleNextStep = () => {
    if (currentStep < 6) {
      // Skip skills selection for customized assessments
      if (currentStep === 2 && assessmentData.assessmentType === 'customized') {
        setCurrentStep(4); // Jump to customized assessment config (replacing Questions step)
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleRoleDetailsSubmit = (role: string, level: 'entry' | 'mid' | 'senior') => {
    setAssessmentData((prev) => ({ ...prev, role, level }));
    handleNextStep();
  };

  const handleAssessmentTypeSelect = (type: AssessmentType) => {
    setAssessmentData((prev) => ({ ...prev, assessmentType: type }));
    
    if (type === 'ai-assisted') {
      // For AI-assisted, show the AI processing animation
      setProcessingMessage({
        title: 'Analyzing Role Requirements',
        description: `Our AI is researching the top skills for ${assessmentData.level} ${assessmentData.role} positions...`
      });
      setShowAIProcessing(true);
      
      setTimeout(() => {
        setShowAIProcessing(false);
        handleNextStep();
      }, 2500);
    } else {
      // For customized, if no skills are selected yet, go to skills selection
      // Otherwise, skip directly to the customization screen
      if (assessmentData.selectedSkills.length === 0) {
        setCurrentStep(3); // Go to skills selection step
      } else {
        handleNextStep(); // Skip to customization
      }
    }
  };

  const handleSkillsSubmit = (skills: string[]) => {
    setAssessmentData((prev) => {
      // Initialize empty skill questions for each selected skill
      const initialSkillQuestions: Record<string, string[]> = {};
      skills.forEach(skill => {
        initialSkillQuestions[skill] = [];
      });
      
      return {
        ...prev, 
        selectedSkills: skills,
        // For AI-assisted path, we still initialize the skillQuestions structure
        // but it will be populated by the AI rather than manually
        skillQuestions: prev.skillQuestions || initialSkillQuestions
      };
    });
    handleNextStep();
  };

  const handleQuestionsSubmit = (customQuestions: string[]) => {
    setAssessmentData((prev) => ({ ...prev, customQuestions }));
    handleNextStep();
  };

  // Add a new handler for the interviewer settings step in AI-assisted flow
  const handleInterviewerSettingsSubmit = () => {
    setAssessmentData((prev) => ({
      ...prev,
      language: interviewerSettings.language,
      accent: interviewerSettings.accent,
      voiceId: interviewerSettings.voiceId,
      // Ensure we keep the skill questions mapping
      skillQuestions: prev.skillQuestions
    }));
    handleNextStep(); // Go to completion step
  };
  
  // Update interviewer settings
  const handleInterviewerSettingsChange = (settings: InterviewerSettings) => {
    setInterviewerSettings(settings);
  };

  const handleCustomConfigSubmit = (config: CustomConfig) => {
    setAssessmentData((prev) => ({ 
      ...prev, 
      customQuestions: config.questions,
      language: config.language,
      accent: config.accent,
      voiceId: config.voiceId,
      // Store the skill-specific questions mapping as JSON in the skill_questions column
      skillQuestions: config.skillQuestions
    }));
    setCurrentStep(6); // Go directly to completion step
  };

  const handleCreateAssessment = async () => {
    if (!user) {
      toast.error('You must be logged in to create an assessment');
      return;
    }

    const hasEnoughCredits = userCredits !== null && userCredits >= 1;
    const assessmentStatus = hasEnoughCredits ? 'active' : 'inactive';

    setIsSubmitting(true);
    
    try {
      console.log('[CREATE ASSESSMENT] Starting assessment creation:', {
        userId: user.id,
        role: assessmentData.role,
        level: assessmentData.level,
        source: assessmentData.source,
        atsJobId: assessmentData.atsJobId,
        assessmentType: assessmentData.assessmentType,
        language: assessmentData.language,
        accent: assessmentData.accent,
        timestamp: new Date().toISOString()
      });

      // First get the user's organization ID
      const { data: userData, error: userError } = await supabase
        .from('profiles')
        .select('organization_id, first_name, last_name')
        .eq('id', user.id)
        .single();

      console.log('[CREATE ASSESSMENT] Fetched user profile:', {
        userData,
        error: userError,
        timestamp: new Date().toISOString()
      });

      if (userError) {
        console.error('[CREATE ASSESSMENT] Error fetching user organization:', {
          error: userError,
          userId: user.id,
          timestamp: new Date().toISOString()
        });
        throw new Error('Failed to get organization details');
      }

      if (!userData?.organization_id) {
        console.error('[CREATE ASSESSMENT] No organization found for user:', {
          userId: user.id,
          userData,
          timestamp: new Date().toISOString()
        });
        throw new Error('No organization found for user');
      }

      console.log('[CREATE ASSESSMENT] Creating assessment with organization:', {
        organizationId: userData.organization_id,
        timestamp: new Date().toISOString()
      });

      // Create the assessment with all required fields
      const { data: assessment, error } = await supabase
        .from('assessments')
        .insert([
          {
            user_id: user.id,
            organization_id: userData.organization_id,
            title: assessmentData.role,
            level: assessmentData.level,
            skills: assessmentData.selectedSkills,
            custom_questions: assessmentData.customQuestions,
            status: assessmentStatus,
            candidates_invited: 0,
            candidates_completed: 0,
            updated_at: new Date().toISOString(),
            source: assessmentData.source,
            ats_job_id: assessmentData.atsJobId,
            assessment_type: assessmentData.assessmentType,
            language: assessmentData.language,
            accent: assessmentData.accent,
            voice_id: assessmentData.voiceId,
            skill_questions: assessmentData.skillQuestions
          }
        ])
        .select()
        .single();

      console.log('[CREATE ASSESSMENT] Assessment creation result:', {
        assessment,
        error,
        timestamp: new Date().toISOString()
      });

      if (error) {
        console.error('[CREATE ASSESSMENT] Error creating assessment:', {
          error,
          assessmentData,
          timestamp: new Date().toISOString()
        });
        throw error;
      }

      if (!assessment) {
        console.error('[CREATE ASSESSMENT] No assessment data returned:', {
          timestamp: new Date().toISOString()
        });
        throw new Error('Failed to create assessment - no data returned');
      }

      console.log('[CREATE ASSESSMENT] Successfully created assessment:', {
        assessmentId: assessment.id,
        timestamp: new Date().toISOString()
      });

      setCreatedAssessmentId(assessment.id);

      if (hasEnoughCredits) {
        toast.success('Assessment created successfully!');
      } else {
        toast.warning('Assessment created but inactive. Add credits to activate it.');
      }
    } catch (error: any) {
      console.error('[CREATE ASSESSMENT] Error in handleCreateAssessment:', {
        error,
        timestamp: new Date().toISOString()
      });
      toast.error(error.message || 'Failed to create assessment');
      setIsSubmitting(false);
      return;
    }
  };

  const handleCopyLink = async () => {
    if (createdAssessmentId) {
      try {
        console.log('[CREATE LINK] Generating assessment link for:', createdAssessmentId);
        
        // Fetch assessment details first
        const { data: assessment, error } = await supabase
          .from('assessments')
          .select(`
            title,
            skills,
            organization_id,
            organizations:organization_id (
              name
            )
          `)
          .eq('id', createdAssessmentId)
          .single();

        if (error) {
          console.error('[CREATE LINK] Error fetching assessment details:', error);
          throw new Error('Failed to generate invite link');
        }

        console.log('[CREATE LINK] Assessment details for link:', assessment);

        // Get organization name with fallback
        let organizationName = 'Unknown Organization';
        
        // Safely extract organization name if available
        if (assessment.organizations) {
          // Type assertion to help TypeScript understand the structure
          const orgData = assessment.organizations as { name?: string };
          if (orgData.name) {
            organizationName = orgData.name;
          }
        }
        
        console.log('[CREATE LINK] Using organization name:', organizationName);

        // Create encrypted token with all necessary details
        const registrationToken = encryptCandidateDetails({
          type: 'registration',
          assessmentId: createdAssessmentId,
          assessmentDetails: {
            title: assessment.title,
            skills: assessment.skills,
            organization: organizationName
          }
        });

        // Create the original long URL
        const longUrl = `${window.location.origin}/assessment/${createdAssessmentId}/landing?token=${registrationToken}`;
        
        try {
          // Import the URL shortener service
          const { shortenUrl } = await import('@/services/url-shortener');
          
          // Set expiration to 30 days (in seconds)
          const expiresIn = 30 * 24 * 60 * 60;
          
          // Try to shorten the URL
          const shortUrl = await shortenUrl(longUrl, expiresIn);
          
          // Copy the shortened URL to clipboard
          navigator.clipboard.writeText(shortUrl);
          toast.success('Shortened assessment link copied to clipboard');
        } catch (shortenError) {
          console.error('[CREATE LINK] Error shortening URL:', shortenError);
          
          // Fall back to the original long URL if shortening fails
          navigator.clipboard.writeText(longUrl);
          toast.success('Assessment link copied to clipboard');
        }
      } catch (error) {
        console.error('[CREATE LINK] Error generating invite link:', error);
        toast.error('Failed to generate invite link');
      }
    }
  };

  // Determine which step component to show
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <RoleDetailsStep
            onSubmit={handleRoleDetailsSubmit}
            initialData={{ role: assessmentData.role, level: assessmentData.level }}
          />
        );
      case 2:
        return (
          <AssessmentTypeStep
            onSelect={handleAssessmentTypeSelect}
            selectedType={assessmentData.assessmentType || null}
            isBasicPlan={isBasicPlan(userSubscription)}
          />
        );
      case 3:
        return (
          <SkillsSelectionStep
            onSubmit={handleSkillsSubmit}
            onBack={handleBack}
            initialSkills={assessmentData.selectedSkills}
            role={assessmentData.role}
            level={assessmentData.level}
            isBasicPlan={isBasicPlan(userSubscription)}
          />
        );
      case 4:
        return assessmentData.assessmentType === 'customized' ? (
          <CustomAssessmentConfig
            onSubmit={handleCustomConfigSubmit}
            selectedSkills={assessmentData.selectedSkills}
            role={assessmentData.role}
            level={assessmentData.level}
            initialGeneralQuestions={assessmentData.customQuestions}
            initialSkillQuestions={assessmentData.skillQuestions}
          />
        ) : (
          <QuestionsStep
            onSubmit={handleQuestionsSubmit}
            onBack={handleBack}
            initialQuestions={assessmentData.customQuestions}
            role={assessmentData.role}
            level={assessmentData.level}
            skills={assessmentData.selectedSkills}
            isBasicPlan={isBasicPlan(userSubscription)}
          />
        );
      case 5:
        return assessmentData.assessmentType === 'customized' ? (
          <CompletionStep 
            assessmentData={assessmentData}
            onCreateAnother={() => {
              setCurrentStep(1);
              setAssessmentData({
                role: '',
                level: 'mid',
                selectedSkills: [],
                customQuestions: [],
                source: 'RoundOne'
              });
            }}
            onComplete={handleCreateAssessment}
            onCopyLink={handleCopyLink}
            isSubmitting={isSubmitting}
            insufficientCredits={userCredits !== null && userCredits < 1}
            onEdit={(step) => setCurrentStep(step)}
            assessmentId={createdAssessmentId}
          />
        ) : (
          <AIAssessmentSettingsStep
            settings={interviewerSettings}
            onSettingsChange={handleInterviewerSettingsChange}
            onSubmit={handleInterviewerSettingsSubmit}
            onBack={handleBack}
            customQuestions={assessmentData.customQuestions}
            role={assessmentData.role}
            level={assessmentData.level}
          />
        );
      case 6:
        return (
          <CompletionStep 
            assessmentData={assessmentData}
            onCreateAnother={() => {
              setCurrentStep(1);
              setAssessmentData({
                role: '',
                level: 'mid',
                selectedSkills: [],
                customQuestions: [],
                source: 'RoundOne'
              });
            }}
            onComplete={handleCreateAssessment}
            onCopyLink={handleCopyLink}
            isSubmitting={isSubmitting}
            insufficientCredits={userCredits !== null && userCredits < 1}
            onEdit={(step) => setCurrentStep(step)}
            assessmentId={createdAssessmentId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 p-0">
        <div className="relative min-h-screen flex flex-col items-center">
          {/* Background gradient */}
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-white to-indigo-50/50 -z-10" />
          
          {/* Header with back button */}
          <div className="w-full p-6">
            <Button 
              variant="ghost" 
              onClick={handleBack}
              className="px-2 py-1 text-[#1A1F2C]/70 hover:text-[#1A1F2C] hover:bg-white/80"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
          
          {/* Progress stepper */}
          <div className="w-full mt-4 mb-10">
            <AssessmentSteps currentStep={currentStep} />
          </div>
          
          {/* Step content */}
          <AnimatePresence mode="wait">
            {showAIProcessing ? (
              <motion.div
                key="processing"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="w-full flex justify-center"
              >
                <AIProcessingAnimation 
                  title={processingMessage.title}
                  description={processingMessage.description}
                />
              </motion.div>
            ) : (
              <motion.div
                key={`step-${currentStep}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="w-full flex justify-center px-4"
              >
                {renderStepContent()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default CreateAssessment;
